import React from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import {
  Shield,
  TrendingUp,
  Users,
  Award,
  CheckCircle2,
  ArrowRight,
  Building2,
  Target,
  Briefcase,
  PiggyBank,
} from "lucide-react";
import { ScrollReveal } from "./ScrollReveal";
import Header from "./Header";
import Footer from "./Footer";
import GoogleReviews from "./googleReviews";

const HomePage = () => {
  const services = [
    {
      icon: PiggyBank,
      title: "Retirement Planning",
      description:
        "Personalized strategies to help you retire with confidence and financial security.",
      link: "/retirement-planning",
      color: "bg-blue-50 text-blue-600",
    },
    {
      icon: TrendingUp,
      title: "Wealth Management",
      description:
        "Comprehensive portfolio management and investment strategies for growing your wealth.",
      link: "/wealth-management",
      color: "bg-green-50 text-green-600",
    },
    {
      icon: Shield,
      title: "Independent Advisor",
      description:
        "Fee-only advisors legally bound to act in your best interest, always.",
      link: "/financial-advisor",
      color: "bg-purple-50 text-purple-600",
    },
    {
      icon: Briefcase,
      title: "Financial Advisor",
      description:
        "Expert guidance for all your financial decisions, from budgeting to estate planning.",
      link: "/financial-advisor",
      color: "bg-amber-50 text-amber-600",
    },
  ];

  const stats = [
    { value: "$500M+", label: "Assets Under Management" },
    { value: "25+", label: "Years of Experience" },
    { value: "1,200+", label: "Families Served" },
    { value: "100%", label: "Commitment" },
  ];

  const testimonials = [
    {
      name: "Chris N",
      location: "OK",
      image:
        "https://ui-avatars.com/api/?name=Chris+N&rounded=true&bold=true&format=svg",
      text: "Over the last 35 years, Curt has demonstrated the flexibility and dynamic knowledge of financial markets to turn what appears to be a questionable situation into success at age 54. I have doubts that I could have done this at all without his help. I recommend him without reservation.",
    },
    {
      name: "Pam S",
      location: "OK",
      image:
        "https://ui-avatars.com/api/?name=Pam+S&rounded=true&bold=true&format=svg",
      text: "Curt Wilson has been my financial advisor/planner for about 6.5 years. During that time I have seen the care he takes with my investments and appreciate greatly the time he spends with me explaining his thought processes and answering all my questions. His education, professionalism and personal sense of responsibility help me feel confident in all my dealings with him.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section
        className="pt-28 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#f8fafb] via-white to-[#f0f7fa]"
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="hero-badge inline-flex items-center gap-2 px-4 py-2 bg-[#e8eef2] rounded-full text-[#3d4f5f] text-sm font-semibold mb-6">
                <Shield className="w-4 h-4" />
                Trusted Advisors • Serving Oklahoma Since 2000
              </div>
              <h1 className="hero-headline text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3d4f5f] mb-6 leading-tight">
                Meet Curtis Wilson, Helping you reach the summit of your
                <br />
                <span className="text-[#4a6274]">financial life</span>
              </h1>
              <p className="hero-subheadline text-lg sm:text-xl text-gray-600 mb-10 max-w-xl">
                Expertise for retirees, near-retirees and business owners. We
                listen to your hopes and goals and propose customized strategies
                based on your unique situation.
              </p>
              <div className="hero-cta flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/contact">
                  <Button
                    className="btn-primary btn-cta-pulse bg-[#3d4f5f] hover:bg-[#2d3e4e] text-white px-8 py-6 text-lg rounded-lg shadow-lg w-full sm:w-auto"
                    data-testid="hero-cta-consultation"
                  >
                    Schedule Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="tel:+19184777227">
                  <Button
                    variant="outline"
                    className="border-2 border-[#3d4f5f] text-[#3d4f5f] px-8 py-6 text-lg rounded-lg hover:bg-[#f8fafb] transition-all duration-200 w-full sm:w-auto"
                    data-testid="hero-cta-call"
                  >
                    Call 918.477.7227
                  </Button>
                </a>
              </div>

              {/* <GoogleReviews />*/}
            </div>

            {/* Hero Image */}
            <div className="hidden lg:block hero-cta">
              <img
                src="https://images.pexels.com/photos/7681091/pexels-photo-7681091.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Professional financial advisors helping clients with wealth management"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
                loading="eager"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section
        className="py-12 px-4 sm:px-6 lg:px-8 bg-[#3d4f5f]"
        data-testid="stats-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-300 mt-8 text-sm">
            *As of <span className="font-semibold">April, 2026.</span>
          </p>
        </div>
      </section>

      {/* Services Section */}
      <ScrollReveal>
        <section
          className="py-20 px-4 sm:px-6 lg:px-8 bg-white"
          data-testid="services-section"
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#3d4f5f] mb-4">
                Our Services
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Comprehensive financial planning and wealth management solutions
                tailored to your unique needs
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => (
                <Link to={service.link} key={index}>
                  <Card
                    className="card-hover p-6 h-full border-2 border-gray-100 hover:border-[#3d4f5f] transition-all duration-200 cursor-pointer group"
                    data-testid={`service-card-${index}`}
                  >
                    <div
                      className={`w-12 h-12 rounded-lg ${service.color} flex items-center justify-center mb-4`}
                    >
                      <service.icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-bold text-[#3d4f5f] mb-2 group-hover:text-[#2d3e4e] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {service.description}
                    </p>
                    <div className="flex items-center text-[#3d4f5f] text-sm font-medium group-hover:text-[#2d3e4e]">
                      Learn More{" "}
                      <ArrowRight className="w-4 h-4 ml-1 arrow-slide" />
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Why Choose Us Section */}
      <ScrollReveal>
        <section
          className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f8fafb]"
          data-testid="why-choose-section"
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#3d4f5f] mb-6">
                  Why Oklahoma Families Choose Us
                </h2>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  For over 25 years, we've helped Oklahoma families achieve
                  their financial goals through personalized planning and
                  guidance.
                </p>
                <div className="space-y-4">
                  {[
                    {
                      icon: Shield,
                      text: "We always put your interests first",
                    },
                    // {
                    //   icon: Award,
                    //   text: "Fee-Only Structure - No hidden commissions or conflicts",
                    // },
                    {
                      icon: Users,
                      text: "Personalized Service - Strategies tailored to your goals",
                    },
                    {
                      icon: Building2,
                      text: "Local Expertise - Deep understanding of Oklahoma markets",
                    },
                    // {
                    //   icon: Target,
                    //   text: "Proven Results - Track record of client success",
                    // },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#4a6274] flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700">{item.text}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8">
                  <Link to="/contact">
                    <Button
                      className="bg-[#4a6274] hover:bg-[#3a5263] text-white px-8 py-4 text-base rounded-lg shadow-lg"
                      data-testid="why-choose-cta"
                    >
                      Start Your Financial Journey
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="hidden lg:block">
                <img
                  src="https://images.pexels.com/photos/5669619/pexels-photo-5669619.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Professional team meeting with clients"
                  className="rounded-2xl shadow-xl w-full h-auto object-cover aspect-[1/1.25]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Testimonials Section */}
      {/* <ScrollReveal>
        <section
          className="py-20 px-4 sm:px-6 lg:px-8 bg-white"
          data-testid="testimonials-section"
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#3d4f5f] mb-4">
                What Our Clients Say
              </h2>
              <p className="text-base sm:text-lg text-gray-600">
                Real stories from Oklahoma families we've helped
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {testimonials.map((testimonial, index) => (
                <Card
                  key={index}
                  className="card-hover p-6 bg-white border border-gray-100"
                  data-testid={`testimonial-card-${index}`}
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg
                        key={star}
                        className="w-5 h-5 fill-[#FBBC05]"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-600 mb-6 text-sm italic">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-semibold text-[#3d4f5f]">
                        {testimonial.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {testimonial.location}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal> */}

      {/* CTA Section */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#3d4f5f] to-[#2d3e4e]"
        data-testid="cta-section"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Secure Your Financial Future?
          </h2>
          <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
            Schedule a no-cost, no-obligation consultation with one of our
            experienced financial advisors today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                className="bg-white text-[#3d4f5f] hover:bg-gray-100 px-8 py-6 text-lg rounded-lg shadow-lg font-semibold w-full sm:w-auto"
                data-testid="cta-consultation-btn"
              >
                Schedule a No-Cost Consultation
              </Button>
            </Link>
            <a href="tel:+19184777227">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-lg w-full sm:w-auto"
                data-testid="cta-call-btn"
              >
                Call 918.477.7227
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
